import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

import { Autocomplete, TextField, type AutocompleteProps } from "@mui/material";

interface CodeInfos {
  code: string;
  value: string;
};

interface CustomAutocompleteProps extends Omit<AutocompleteProps<CodeInfos, false, false, true>, "value" | "onChange" | "renderInput" | "options"> {
  options: CodeInfos[];
  value: string;
  onChange: (code: string) => void;
  label?: string;
}

function _CustomAutocomplete({ options, value, onChange, label, ...rest }: CustomAutocompleteProps) {
  const toCode = (text: string) => options.find((o) => o.code === text || o.value === text)?.code ?? text;

  return (
    <Autocomplete<CodeInfos, false, false, true>
      {...rest}
      freeSolo
      options={options}
      value={options.find((o) => o.code === value) ?? value ?? null}
      onChange={(_, newValue) => {
        onChange(typeof newValue === "string" ? toCode(newValue) : newValue?.code ?? "");
      }}
      onBlur={(e) => {
        const code = toCode((e.target as HTMLInputElement).value);
        if (code !== value) onChange(code);
      }}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.value)}
      isOptionEqualToValue={(option, val) =>
        typeof val === "string" ? option.code === val : option.code === val.code
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="standard"
          sx={{ "& .MuiAutocomplete-input": { paddingLeft: "8px !important" } }}
        />
      )}
      slotProps={{ listbox: { sx: { "& .MuiAutocomplete-option": { paddingLeft: "8px !important" } } } }}
    />
  );
}

export const CustomAutocomplete = React.memo(_CustomAutocomplete);
